import { Typography } from "@mui/material";
import { Stack } from "@mui/system";

import CheckIcon from "@mui/icons-material/Check";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../../assets/styles/tokens";
import { useEffect, useMemo, useState } from "react";
import BudgetShortBy from "./BudgetShortBy";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedBrandForBudget } from "../../../../../../../../../features/businessLeaderDashboardSlice";
import { useSearchParams } from "react-router-dom";

const BudgetSearchFilter = () => {
  // Add local state to track input value
  const [inputValue, setInputValue] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { budgetDataListMobile, selectedBrandForBudget, sortForBudget } =
    useSelector((state: any) => state.businessLeaderDashBoard);

  const uniqueBrands = useMemo(() => {
    const set = new Set<string>();
    (budgetDataListMobile || []).forEach((r: any) => {
      if (typeof r?.brand === "string") set.add(r.brand.trim());
    });
    return Array.from(set);
  }, [budgetDataListMobile]);

  //-- this hold search params in url
  useEffect(() => {
    // Update selectedTemplate when searchParams change (e.g., on back/forward navigation)
    dispatch(
      setSelectedBrandForBudget(searchParams.get("searchBrandForBudget") || ""),
    );
    // eslint-disable-next-line
  }, []);
  const isSelected = Boolean(inputValue);
  return (
    <>
      <Typography
        variant="body1"
        sx={{ mt: 1, fontWeight: typography.Font_weight.Medium.$value }}
      >
        {sortForBudget.label}
      </Typography>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mt={1}
        sx={{
          mb: spacing.Spacing.md.$value,
        }}
      >
        <Autocomplete
          fullWidth
          disablePortal
          freeSolo
          data-tracker="660"
          options={uniqueBrands}
          inputValue={inputValue}
          onInputChange={(_, newInputValue) => {
            setInputValue(newInputValue);
          }}
          value={
            uniqueBrands.find(
              (item) => item.trim() === selectedBrandForBudget,
            ) ||
            selectedBrandForBudget || // ← this is the fix: show typed value in box
            null
          }
          popupIcon={null}
          onChange={(_, newValue: string | null) => {
            dispatch(setSelectedBrandForBudget(newValue ?? ""));
            setInputValue(newValue ?? ""); // sync local input state
            setSearchParams(
              (prevParams) => {
                const updatedParams = Object.fromEntries(prevParams);
                if (newValue) {
                  updatedParams.searchBrandForBudget = newValue;
                  //updatedParams.page = "0";
                } else {
                  delete updatedParams.searchBrandForBudget;
                }
                return updatedParams;
              },
              { replace: true },
            );
          }}
          sx={{
            width: "85%",
            "& .MuiInputBase-input": {
              fontSize: typography.Size.Text.md.$value,
              lineHeight: typography.Line_height.text.md.$value,
              fontWeight: typography.Font_weight.Medium.$value,
            },
            // ↓ Always show clear button when input has value
            "& .MuiAutocomplete-clearIndicator": {
              visibility: inputValue ? "visible" : "hidden",
            },
            "& .MuiOutlinedInput-root": {
              paddingRight: spacing.Spacing.none.$value + "!important",
              height: spacing.Spacing["6xl"].$value,
              borderRadius: spacing.Radius.md.$value,
              "&.Mui-focused fieldset": {
                borderColor: isSelected
                  ? colors.Brand[500].$value
                  : colors.GrayLightMode[300].$value,
                borderWidth: "1px",
              },
              "&:not(.Mui-focused) fieldset": {
                borderColor: isSelected
                  ? colors.Brand[500].$value
                  : colors.GrayLightMode[300].$value,
              },
              "& .MuiAutocomplete-paper": {
                border: `1px solid ${colors.GrayLightMode[300].$value}`,
                borderRadius: spacing.Radius.md.$value,
                boxShadow: "none", // optional
              },
            },
          }}
          size="medium"
          renderOption={(props, option, { selected }) => {
            const { key, ...rest } = props;

            return (
              <li
                key={key}
                {...rest}
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: spacing.Spacing["5xl"].$value,
                  position: "relative",
                  backgroundColor: selected ? colors.Brand[50].$value : "",
                }}
              >
                {selected && (
                  <CheckIcon
                    fontSize="small"
                    sx={{
                      position: "absolute",
                      left: spacing.Spacing.lg.$value,
                    }}
                  />
                )}
                <span>{option}</span>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              color="secondary"
              placeholder="Search for specific brand..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {/* Show search icon ONLY when input is empty */}
                    {!inputValue && (
                      <SearchOutlinedIcon
                        sx={{
                          color: colors.GrayLightMode[400].$value,
                          cursor: "pointer",
                          mr: spacing.Spacing.sm.$value,
                        }}
                      />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              const inputVal = (e.target as HTMLInputElement).value.trim();

              if (inputVal) {
                dispatch(setSelectedBrandForBudget(inputVal));
                setInputValue(inputVal); // ← sync so it stays visible in the box

                setSearchParams(
                  (prevParams) => {
                    const updatedParams = Object.fromEntries(prevParams);
                    updatedParams.searchBrandForBudget = inputVal;
                    //updatedParams.page = "0";
                    return updatedParams;
                  },
                  { replace: true },
                );

                (e.target as HTMLInputElement).blur();
              }
            }
          }}
        />
        <BudgetShortBy />
      </Stack>
    </>
  );
};

export default BudgetSearchFilter;
